import React, { useMemo, useState } from "react";
import styled from "styled-components";
import BasicButton from "../basics/BasicButton";
import {
  type DeliveryRow,
  type ReachIssue,
  type ReachUser,
  useGetNotificationReachQuery,
  useLazyGetUserDeliveriesQuery,
} from "../../services/rtkApi/notificationReachApi";

// "who gets notifications": one row per site user with their channels (telegram, push
// devices, installed app), permission state and what happened to their recent notifications

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`;

const Hint = styled.p`
  font-size: 0.85rem;
  color: #555;
  margin: 0 0 12px;
  line-height: 1.4;
`;

const Scroll = styled.div`
  overflow-x: auto;
  max-width: 100%;
  max-height: 640px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  th,
  td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }
  th {
    font-weight: 600;
    color: #333;
    position: sticky;
    top: 0;
    background: #fafafa;
    z-index: 1;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const Chip = styled.button<{ $active?: boolean }>`
  border: 1px solid ${(p) => (p.$active ? "#333" : "#ddd")};
  background: ${(p) => (p.$active ? "#333" : "#fafafa")};
  color: ${(p) => (p.$active ? "#fff" : "#333")};
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.8rem;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
`;

type Tone = "ok" | "warn" | "bad" | "neutral";

function pill(tone: Tone): React.CSSProperties {
  const base: React.CSSProperties = {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 6,
    fontSize: "0.75rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  };
  if (tone === "ok") return { ...base, background: "#d4edda", color: "#155724" };
  if (tone === "warn") return { ...base, background: "#fff3cd", color: "#856404" };
  if (tone === "bad") return { ...base, background: "#f8d7da", color: "#721c24" };
  return { ...base, background: "#e9ecef", color: "#333" };
}

const ISSUE_TEXT: Record<ReachIssue, string> = {
  tg_blocked: "Blocked the Telegram bot",
  tg_muted: "Muted everything in the Telegram bot",
  tg_service_muted: "Muted some services in the Telegram bot",
  tg_link_by_username:
    "Telegram link was saved under their login id; it fixes itself on their next visit",
  push_lost: "Push stopped working on their device",
  permission_denied: "Blocked notifications in the browser",
  permission_without_subscription:
    "Allows notifications but the device has no subscription (one tap on Profile fixes it)",
  installed_without_push: "Has the app installed, push is off",
  push_unconfirmed: "Pushes went out, no device has confirmed one yet",
};

const LEG_REASON: Record<string, string> = {
  not_linked: "no link",
  not_linked_or_muted: "no link",
  muted: "muted",
  blocked: "blocked",
  not_attempted: "not tried",
  no_devices: "no devices",
  gone: "device gone",
  not_configured: "off",
  error: "error",
  pending: "…",
};

function day(ts?: string | null): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function stamp(ts?: string | null): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function legText(leg: { status: string; reason?: string } | null | undefined): string {
  if (!leg) return "—";
  if (leg.status === "sent") return "sent";
  return LEG_REASON[leg.reason ?? ""] ?? leg.reason ?? leg.status;
}

function legTone(leg: { status: string } | null | undefined): Tone {
  if (!leg) return "neutral";
  if (leg.status === "sent") return "ok";
  if (leg.status === "failed") return "bad";
  return "neutral";
}

function reachBadge(u: ReachUser): { text: string; tone: Tone } {
  const tg = u.telegramState === "ok";
  const push = u.pushState === "ok";
  if (tg && push) return { text: "Push + Telegram", tone: "ok" };
  if (push) return { text: "Push", tone: "ok" };
  if (tg) return { text: "Telegram", tone: "ok" };
  return { text: "Not reachable", tone: "bad" };
}

function telegramCell(u: ReachUser): { text: string; tone: Tone; title?: string } {
  if (!u.telegram) return { text: "—", tone: "neutral" };
  const handle = u.telegram.username ? `@${u.telegram.username}` : u.telegram.firstName || "linked";
  if (u.telegramState === "blocked") {
    return { text: `blocked ${day(u.telegram.blockedAt)}`, tone: "bad", title: handle };
  }
  if (u.telegramState === "muted") return { text: "muted", tone: "warn", title: handle };
  return { text: handle, tone: "ok" };
}

function pushCell(u: ReachUser): { text: string; tone: Tone } {
  if (u.pushState === "ok") {
    const platforms = Array.from(
      new Set(u.pushSubscriptions.filter((s) => s.active).map((s) => s.platform)),
    ).join(", ");
    return {
      text: `${u.activePushDevices} device${u.activePushDevices === 1 ? "" : "s"} (${platforms})`,
      tone: "ok",
    };
  }
  if (u.pushState === "lost") {
    const lostAt = u.pushSubscriptions
      .map((s) => s.prunedAt)
      .filter(Boolean)
      .sort()
      .pop();
    return { text: `lost ${day(lostAt)}`, tone: "bad" };
  }
  return { text: "—", tone: "neutral" };
}

function appCell(u: ReachUser): string {
  if (u.devices.length === 0) return "not seen yet";
  if (u.appInstalled) {
    const platforms = Array.from(
      new Set(u.devices.filter((d) => d.lastStandaloneAt || d.standalone).map((d) => d.platform)),
    ).join(", ");
    return `installed (${platforms}) · opened ${day(u.lastStandaloneAt)}`;
  }
  return "browser only";
}

const PERMISSION_TEXT: Record<ReachUser["permission"], { text: string; tone: Tone }> = {
  granted: { text: "allowed", tone: "ok" },
  default: { text: "never asked", tone: "neutral" },
  denied: { text: "blocked", tone: "bad" },
  unsupported: { text: "unsupported", tone: "neutral" },
  unknown: { text: "—", tone: "neutral" },
};

type Filter = "all" | "unreachable" | "reachable" | "issues";

const DeliveriesPanel: React.FC<{ rows: DeliveryRow[] | undefined; loading: boolean }> = ({
  rows,
  loading,
}) => {
  if (loading) return <Hint>Loading deliveries…</Hint>;
  if (!rows || rows.length === 0) {
    return <Hint>No deliveries logged for this person in the last 30 days.</Hint>;
  }
  return (
    <Table>
      <thead>
        <tr>
          <th>When</th>
          <th>Code</th>
          <th>Message</th>
          <th>Telegram</th>
          <th>Push</th>
          <th>On the device</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r._id}>
            <td style={{ whiteSpace: "nowrap" }}>{stamp(r.createdAt)}</td>
            <td>
              <code style={{ fontSize: "0.72rem" }}>{r.code || r.kind}</code>
            </td>
            <td style={{ maxWidth: 280, whiteSpace: "normal" }}>{r.preview || "—"}</td>
            <td>
              <span style={pill(legTone(r.tg))}>{legText(r.tg)}</span>
            </td>
            <td>
              <span style={pill(legTone(r.push))}>{legText(r.push)}</span>
              {r.push?.devices > 1 && (
                <span style={{ color: "#777", marginLeft: 4 }}>
                  {r.push.accepted}/{r.push.devices}
                </span>
              )}
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
              {r.clickedAt ? (
                <span style={pill("ok")}>opened {stamp(r.clickedAt)}</span>
              ) : r.shownAt ? (
                <span style={pill("ok")}>shown {stamp(r.shownAt)}</span>
              ) : r.suppressedAt ? (
                <span style={pill("neutral")}>in app (toast)</span>
              ) : r.push?.status === "sent" ? (
                <span style={{ color: "#777" }}>no receipt</span>
              ) : (
                "—"
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const NotificationReachCard: React.FC = () => {
  const [days, setDays] = useState(14);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [openUser, setOpenUser] = useState<string | null>(null);
  const { data, isFetching, isError, refetch } = useGetNotificationReachQuery({ days });
  const [loadDeliveries, deliveries] = useLazyGetUserDeliveriesQuery();

  const report = data?.data;
  const summary = report?.summary;

  const rows = useMemo(() => {
    const all = report?.users ?? [];
    const q = search.trim().toLowerCase();
    return all
      .filter((u) => {
        if (filter === "unreachable" && u.reachable) return false;
        if (filter === "reachable" && !u.reachable) return false;
        if (filter === "issues" && u.issues.length === 0) return false;
        return !q || u.username.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        // reachable people with problems first, then everyone else by recent activity
        if (a.issues.length !== b.issues.length) return b.issues.length - a.issues.length;
        return (b.lastSignedAt ?? "").localeCompare(a.lastSignedAt ?? "");
      });
  }, [report, filter, search]);

  const toggleUser = (userId: string) => {
    if (openUser === userId) {
      setOpenUser(null);
      return;
    }
    setOpenUser(userId);
    void loadDeliveries({ userId, limit: 40 });
  };

  return (
    <Card className="nadm-card">
      <div
        className="nadm-row"
        style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
      >
        <h2 style={{ margin: 0, fontSize: "1.05rem" }}>Who gets notifications</h2>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          style={{ border: "1px solid #ccc", borderRadius: 4, padding: "4px 6px", background: "#fff" }}
        >
          <option value={7}>last 7 days</option>
          <option value={14}>last 14 days</option>
          <option value={30}>last 30 days</option>
        </select>
        <BasicButton text={isFetching ? "Loading…" : "Refresh"} onClick={() => void refetch()} />
      </div>
      <Hint style={{ marginTop: 8 }}>
        A person is reachable when they have a working Telegram link or at least one device with
        push. &quot;Shown&quot; and &quot;opened&quot; come from the device itself, so they appear
        once that device has opened the updated app. Click a row for their recent notifications.
      </Hint>

      {isError && <Hint style={{ color: "#721c24" }}>Could not load reach data from the messenger.</Hint>}

      {summary && (
        <Chips>
          <Chip $active={filter === "all"} onClick={() => setFilter("all")}>
            {summary.reachable} of {summary.users} reachable
          </Chip>
          <Chip $active={filter === "unreachable"} onClick={() => setFilter("unreachable")}>
            {summary.none} not reachable
          </Chip>
          <Chip $active={filter === "reachable"} onClick={() => setFilter("reachable")}>
            push {summary.pushOnly + summary.both} · telegram {summary.telegramOnly + summary.both}
          </Chip>
          <Chip $active={filter === "issues"} onClick={() => setFilter("issues")}>
            {summary.withIssues} with problems
          </Chip>
          <Chip as="span" style={{ cursor: "default" }}>
            app installed {summary.appInstalled}
          </Chip>
          <Chip as="span" style={{ cursor: "default" }}>
            notifications blocked {summary.permissionDenied}
          </Chip>
        </Chips>
      )}

      <input
        type="search"
        placeholder="Find a user"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="nadm-input-full"
        style={{
          border: "1px solid #ccc",
          borderRadius: 4,
          padding: "6px 8px",
          marginBottom: 10,
          width: 240,
          maxWidth: "100%",
        }}
      />

      <Scroll className="nadm-table-wrap">
        <Table>
          <thead>
            <tr>
              <th>User</th>
              <th>Reach</th>
              <th>Telegram</th>
              <th>Push</th>
              <th>App</th>
              <th>Permission</th>
              <th style={{ textAlign: "right" }}>Sent</th>
              <th style={{ textAlign: "right" }}>Got it</th>
              <th style={{ textAlign: "right" }}>Seen</th>
              <th>Last notification</th>
              <th>Problems</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => {
              const reach = reachBadge(u);
              const tg = telegramCell(u);
              const push = pushCell(u);
              const perm = PERMISSION_TEXT[u.permission];
              const open = openUser === u.userId;
              return (
                <React.Fragment key={u.userId}>
                  <tr
                    onClick={() => toggleUser(u.userId)}
                    style={{ cursor: "pointer", background: open ? "#fff7ef" : undefined }}
                  >
                    <td style={{ whiteSpace: "nowrap" }}>
                      {u.userEmoji ? `${u.userEmoji} ` : ""}
                      <strong>{u.username}</strong>
                      <div style={{ color: "#888", fontSize: "0.72rem" }}>
                        signed in {day(u.lastSignedAt)}
                      </div>
                    </td>
                    <td>
                      <span style={pill(reach.tone)}>{reach.text}</span>
                    </td>
                    <td>
                      <span style={pill(tg.tone)} title={tg.title}>
                        {tg.text}
                      </span>
                    </td>
                    <td>
                      <span style={pill(push.tone)}>{push.text}</span>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{appCell(u)}</td>
                    <td>
                      <span style={pill(perm.tone)}>{perm.text}</span>
                    </td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {u.stats.total}
                    </td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {u.stats.delivered}
                    </td>
                    <td
                      style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                      title="Pushes the device confirmed it showed. ? = pushes went out, no receipt yet"
                    >
                      {u.stats.shown > 0 ? u.stats.shown : u.stats.viaPush > 0 ? "?" : "—"}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {u.last ? (
                        <>
                          <span style={pill(u.last.delivered ? "ok" : "bad")}>
                            {u.last.delivered ? "got it" : "missed"}
                          </span>{" "}
                          {stamp(u.last.at)}
                          <div style={{ color: "#888", fontSize: "0.72rem" }}>{u.last.code}</div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={{ minWidth: 220, whiteSpace: "normal" }}>
                      {u.issues.length === 0
                        ? "—"
                        : u.issues.map((i) => <div key={i}>{ISSUE_TEXT[i] ?? i}</div>)}
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={11} style={{ background: "#fffaf5" }}>
                        <DeliveriesPanel
                          rows={deliveries.originalArgs?.userId === u.userId ? deliveries.data?.data : undefined}
                          loading={deliveries.isFetching}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </Scroll>

      {report && report.orphans.length > 0 && (
        <Hint style={{ marginTop: 10 }}>
          {report.orphans.length} messenger record
          {report.orphans.length === 1 ? "" : "s"} match no site user (
          {report.orphans
            .map((o) => o.telegramUsername ? `@${o.telegramUsername}` : o.siteUsername || o.userId)
            .join(", ")}
          ).
        </Hint>
      )}
    </Card>
  );
};

export default NotificationReachCard;
